import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import TopupDetailCard from "@/features/topup-history/TopupDetailCard";
import { useEffect, useState } from "react";
import type { TopupHistoryDetailData } from "@/types/topup/historydetail";
import { TopupHistoryDetail } from "@/services/topup.service";
import { useAuthStore } from "@/stores/auth.store";
import { toast } from "sonner";
import TopupDetailCardSkeleton from "@/features/topup-history/TopupHistoryDetailSkeleton";
import { handleDownloadTopupReceipt } from "@/hooks/downloadTopupReceipt";

export default function TopupHistoryDetailPage() {
  const navigate = useNavigate();
  const { topUpNo } = useParams<{ topUpNo: string }>();

  const [topupDetail, setTopupDetail] = useState<TopupHistoryDetailData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  useEffect(() => {
    if (!topUpNo) return;

    const fetchTopupDetails = async () => {
      try {
        setIsLoading(true);

        const response = await TopupHistoryDetail({
          agentCompanySellerId:
            useAuthStore.getState().user?.agentCompanySellerId || "",
          topUpNo,
        });

        await new Promise((resolve) => setTimeout(resolve, 300));

        setTopupDetail(response.data);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to load topup details.",
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchTopupDetails();
  }, [topUpNo]);

  const handleDownloadReceipt = async () => {
      if (!topUpNo || downloadingReceipt) return;
  
      try {
        setDownloadingReceipt(true);
  
        await handleDownloadTopupReceipt(topUpNo);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to download booking confirmation.",
        );
      } finally {
        setDownloadingReceipt(false);
      }
    };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between md:flex-row md:items-start">
        <div className="flex flex-1 items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="cursor-pointer flex items-center gap-2 text-lg font-semibold text-secondary-foreground md:text-xl"
          >
            <ChevronLeft
              size={24}
              className="text-primary"
              aria-hidden="true"
            />
            Topup no. {topupDetail?.topUpNo}
          </button>
        </div>

        <div className="flex-1 md:flex md:flex-row md:justify-end">
          <Button
            type="button"
            variant="default"
            className="w-full cursor-pointer mt-4 md:w-2xs md:mt-0"
            onClick={handleDownloadReceipt}
            disabled={downloadingReceipt}
          >
            {downloadingReceipt ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                Download Receipt
              </>
            )}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <TopupDetailCardSkeleton />
      ) : (
        <TopupDetailCard topup={topupDetail} />
      )}
    </div>
  );
}
